// @ts-nocheck — large canvas studio shared across two Vite apps; UI kits differ slightly per host.
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { 
  Brain, 
  Sparkles, 
  Wrench, 
  Search, 
  Play, 
  Pause, 
  RotateCcw, 
  FileText, 
  ExternalLink, 
  CheckCircle2, 
  Layers,
  Eye, 
  EyeOff,
  Zap,
  Activity,
  FolderTree,
  ChevronRight,
  Info,
  X,
  Plus,
  Save,
  Sliders,
  Check,
  Compass,
  Maximize2,
  RefreshCw,
  HelpCircle,
  FolderOpen,
  Grid,
  ShieldAlert,
  Edit3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export type BrainDomain = "prometheus" | "aura";

export interface SourceGroup {
  id: string;
  name: string;
  color: string;
  count: number;
  active: boolean;
}

export interface DetailedNode {
  id: string;
  title: string;
  sourceId: string;
  sourceName: string;
  color: string;
  x: number;
  y: number;
  z: number;
  cluster: string;
  isKeyNode?: boolean;
  needsAttention?: boolean;
  isLiveMemory?: boolean;
  /** Workshop notes saved from Prometheus brain API / localStorage */
  isLiveSaved?: boolean;
  created_at?: string;
  filename: string;
  description: string;
  content: string;
  connections: string[];
}

export interface LiveMemoryGraphData {
  nodes?: Array<{
    id: string;
    category: string;
    label?: string;
    title?: string;
    color?: string;
    created_at?: string;
    content?: string;
  }>;
  edges?: Array<{
    source: string;
    target: string;
    relation?: string;
    weight?: number;
  }>;
  stats?: { total_nodes: number; total_edges: number };
}

export const BASE_AURA_SOURCES: SourceGroup[] = [
  { id: "values", name: "Wiki Personal & Valores", color: "#38bdf8", count: 12, active: true },
  { id: "habits", name: "Hábitos & Bienestar", color: "#fb7185", count: 24, active: true },
  { id: "nano", name: "Nano Aventuras (Fotos PR)", color: "#ec4899", count: 16, active: true },
  { id: "memory", name: "Memoria de Vida & Diario", color: "#f59e0b", count: 20, active: true },
  { id: "prod", name: "Productividad & HD 6405", color: "#00e5ff", count: 15, active: true },
  { id: "projects", name: "Proyectos de Vida", color: "#10b981", count: 14, active: true },
  { id: "chief", name: "Agentes & Asistente Ejecutivo", color: "#a855f7", count: 8, active: true },
];

export const mapCategoryToAuraSource = (cat: string): string => {
  const c = (cat || "").toLowerCase();
  if (c.includes("valor") || c.includes("filosof") || c.includes("personal") || c.includes("etica") || c.includes("principio")) return "values";
  if (c.includes("habit") || c.includes("salud") || c.includes("bienestar") || c.includes("agua") || c.includes("ejercicio") || c.includes("sueno") || c.includes("nutri")) return "habits";
  if (c.includes("foto") || c.includes("nano") || c.includes("viaje") || c.includes("aventura") || c.includes("paisaje")) return "nano";
  if (c.includes("trabajo") || c.includes("prod") || c.includes("foco") || c.includes("hd") || c.includes("monitor") || c.includes("oficina")) return "prod";
  if (c.includes("proyect") || c.includes("finanz") || c.includes("meta") || c.includes("taller") || c.includes("casa")) return "projects";
  if (c.includes("agent") || c.includes("chief") || c.includes("asistent") || c.includes("auditor") || c.includes("tecnic") || c.includes("ia") || c.includes("cerebro")) return "chief";
  return "memory";
};

interface SecondBrain3DStudioProps {
  initialDomain?: BrainDomain;
  liveData?: LiveMemoryGraphData;
  /** Extra nodes (e.g. Prometheus brain-notes) injected by the host app */
  extraNodes?: DetailedNode[];
}

export const SecondBrain3DStudio: React.FC<SecondBrain3DStudioProps> = ({ 
  initialDomain = "aura",
  liveData,
  extraNodes = [],
}) => {
  const [domain, setDomain] = useState<BrainDomain>(initialDomain);
  const [selectedNode, setSelectedNode] = useState<DetailedNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<DetailedNode | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCinemaMode, setIsCinemaMode] = useState(true);
  const [isPlayingDemo, setIsPlayingDemo] = useState(false);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);
  const [showWhatsInHere, setShowWhatsInHere] = useState(false);
  const [showSourcesMobile, setShowSourcesMobile] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedMarkdown, setEditedMarkdown] = useState("");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // 3D Camera & Orbit State
  const rotationRef = useRef({ rotX: 0.22, rotY: 0.65 });
  const zoomRef = useRef(1.0);
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const hasMovedSignificantlyRef = useRef(false);
  const demoProgressRef = useRef(0);
  const inertiaRef = useRef({ vx: 0, vy: 0 });

  // Prometheus Independent Engineering Sources (Specialized to workshop: 188 nodes total)
  const [prometheusSources, setPrometheusSources] = useState<SourceGroup[]>([
    { id: "wiki", name: "Normas ASCE 7-22 & PR", color: "#38bdf8", count: 18, active: true },
    { id: "cad", name: "Diseño CAD & Sólidos 3D", color: "#fb7185", count: 42, active: true },
    { id: "firmware", name: "Firmware ESP32 & LoRa 915MHz", color: "#ec4899", count: 35, active: true },
    { id: "memory", name: "Bitácora & Ensayos MOC", color: "#f59e0b", count: 26, active: true },
    { id: "solar", name: "Microred 48V LiFePO4", color: "#00e5ff", count: 24, active: true },
    { id: "projects", name: "Proyectos de Taller", color: "#10b981", count: 31, active: true },
    { id: "titans", name: "Consejo de Titanes", color: "#a855f7", count: 12, active: true },
  ]);

  const [auraSources, setAuraSources] = useState<SourceGroup[]>(BASE_AURA_SOURCES);

  // Sync live Aura sources: keep the 7 master pillars and augment counts with real user memories
  useEffect(() => {
    if (!liveData?.nodes || liveData.nodes.length === 0) {
      setAuraSources(BASE_AURA_SOURCES);
      return;
    }

    const liveCounts: Record<string, number> = {};
    liveData.nodes.forEach((n) => {
      const sId = mapCategoryToAuraSource(n.category || "");
      liveCounts[sId] = (liveCounts[sId] || 0) + 1;
    });

    setAuraSources((prev) =>
      prev.map((src) => {
        const baseItem = BASE_AURA_SOURCES.find((b) => b.id === src.id) || src;
        const liveAdd = liveCounts[src.id] || 0;
        return {
          ...src,
          count: baseItem.count + liveAdd,
        };
      })
    );
  }, [liveData]);

  const currentSources = domain === "prometheus" ? prometheusSources : auraSources;
  const setSources = domain === "prometheus" ? setPrometheusSources : setAuraSources;

  // Toggle a source group on/off
  const toggleSource = (sourceId: string) => {
    setSources((prev) =>
      prev.map((s) => (s.id === sourceId ? { ...s, active: !s.active } : s))
    );
  };

  // Curated real-world topics for rich node simulation in both domains
  const AURA_TOPICS: Record<string, { titles: string[]; descriptions: string[] }> = {
    values: {
      titles: [
        "Brújula Moral & Ética Estoica",
        "Paz Mental & Cero Deuda Cognitiva",
        "Integridad y Palabra Empeñada",
        "Lealtad a la Verdad & Cero Quejas sin Solución",
        "Vínculo y Lealtad Familiar en PR",
        "Autonomía & Resiliencia en Puerto Rico",
        "Cuidado Quiropráctico & Alineación (6 Años)",
        "100% Comida Real: Cero Químicos & Longevidad 100+",
        "Regla del Creador: Presente vs Captura",
        "Puntualidad Consciente (Llegar 15-30m Temprano)",
        "Santuario Personal ante Desorden Ajeno",
        "Solo una Cosa (The ONE Thing - Gary Keller)",
        "Despertar Disciplinado (Club de las 5 AM)",
        "Retomar Hábitos Atómicos (James Clear)",
        "Mente de Principiante (Shoshin)",
        "Amor Fati: Aceptación y Fortaleza",
        "Meditaciones de Marco Aurelio",
        "Desconexión Digital & Serenidad",
        "Propósito y Enfoque a Largo Plazo",
        "Claridad Mental Matutina (Ventana 2 Horas)",
        "Autocontrol & Templanza Emocional ante la Incertidumbre",
        "Reflexión Filosófica Diaria",
        "Principio de Acción Deliberada",
        "Armonía entre Esfuerzo y Descanso",
        "Minimalismo y Orden Esencial",
        "Humildad y Aprendizaje Continuo"
      ],
      descriptions: [
        "Principio rector para mantener claridad y serenidad ante la incertidumbre.",
        "Directriz de vida enfocada en evitar sobrecarga cognitiva y proteger la paz interior.",
        "Compromiso inquebrantable con la autenticidad y los valores personales.",
        "Regla vital: priorizar lo que está bajo nuestro control directo."
      ]
    },
    habits: {
      titles: [
        "Trinidad Matutina: Respiración, Mantras y Desayuno",
        "Desayuno Maestro: Huevos, Romero, Queso & Fruta",
        "Hidratación: 3L Agua con Limón Criollo & Electrolitos",
        "Sueño Óptimo: 7.5 a 8 Horas (5 Ciclos 90 min)",
        "Lectura Diaria: 20 min de Enfoque y Filosofía",
        "Guitarra Yamaha Acústica: Arpegios y Calma",
        "Co-Working & Estudio con Christian en Starbucks",
        "Entrenamiento al Aire Libre: Pistas Bayamón, Comerío & Cidra",
        "Calistenia Ligera: Flexiones & Fondos",
        "Estiramientos Dinámicos de Espalda y Cuello",
        "Rutina de Movilidad Articular Matutina",
        "Caminata al Aire Libre & Sol Matutino",
        "Higiene Postural en Estación HD 6405",
        "Nudos de Escalada & Estiba en Home Depot 6405",
        "Nudo As de Guía Rápido con Firme",
        "Nudo Ballestrinque para Amarre Seguro",
        "Nudo Ocho Doble de Escalada y Rescate",
        "Nudo Vuelta de Escota para Dos Cabos",
        "Micro-Limpieza Diaria (Cuarto y Corolla LE 2020)",
        "Mantenimiento del Huerto en Mesa Levantada",
        "Cosecha Activa: Limones, Pana, Aguacate & Recao",
        "Nutrición Zonas Azules: Ensaladas & Garbanzos",
        "Aceite de Oliva Extra Virgen & Semillas",
        "Infusión Nocturna de Manzanilla y Matcha",
        "Respiración Diafragmática 4-7-8 al Dormir",
        "Check-in de Energía al Despertar",
        "Pausa Activa de 5 min por Bloque de Foco",
        "Higiene Digital: Purga de Tarjetas SD y Fotos",
        "Timer de 90 min: Despegue de Pantalla HP Omen",
        "Alerta Consciente: Cero Azúcar & Cero Mantecado",
        "Llamada a Mami & Vínculo Familiar con Hermana",
        "Visitas a Abuelos: Sierra de Mesa & Herramientas",
        "Registro Diario de Gastos y Flujo de Caja",
        "Inspección Preventiva: Cisterna & Generador",
        "Mantenimiento Vehicular: Filtros STP & RainX",
        "Vaciado Nocturno de 2 Minutos (Cero Rumiar)"
      ],
      descriptions: [
        "Hábito clave para mantener niveles óptimos de energía y rendimiento diario.",
        "Práctica manual para cultivar destreza fina y serenidad mental.",
        "Pilar de bienestar físico y salud postural en el día a día.",
        "Rutina de longevidad inspirada en hábitos de las Zonas Azules y micro-hábitos de fricción cero."
      ]
    },
    nano: {
      titles: [
        "Arsenal Pro: Sony A7 III + Sony GM 24-105mm F4",
        "Cinematografía Aérea: Drone DJI Air 3 en PR",
        "Tomas en Acción & Agua: DJI Action 4",
        "Estabilización Ronin RS 3 & Filtros K&F ND 3-1000",
        "Audio de Campo: HollyLand Lark Max & RODE VideoMic",
        "Laboratorio de Impresión: Canon Selphy 1500 & IVY",
        "Pipeline Comercial: Bodas, Quinceañeros & Eventos",
        "Cobro y Registro Formal de Depósitos con ATH Móvil",
        "Voz Editorial: Reflexión, Naturaleza y Aventura",
        "Regla de Oro: Disfrutar la Aventura sin Ser Esclavo del Lente",
        "Rutas Transitables Corolla LE 2020: Pistas & Costa",
        "Meta de Facturación: Alcanzar +$2,000/mes en Eventos",
        "Captura Log en iPhone 16 Pro Max (ProRes)",
        "Edición & Revelado en Adobe Lightroom Classic",
        "Optimización de Archivos con JPEGmini Pro",
        "Cueva Ventana (Arecibo) - Golden Hour",
        "Mar Chiquita (Manatí) - Olas & Rompeolas",
        "Río Abajo (Utuado) - Paisaje de Mogotes",
        "Faro Los Morrillos (Cabo Rojo) - Acantilados",
        "Playa Buyé (Cabo Rojo) - Mar Turquesa",
        "Bosque de Guajataca - Sendero de Helechos",
        "Cascada Gozalandia (San Sebastián)",
        "Cerro Punta (Jayuya) - Cordillera Central",
        "Playa Flamenco (Culebra) - Arena Blanca",
        "Bahía Mosquito (Vieques) - Bioluminiscencia",
        "Charco Azul (Patillas) - Poza de Río",
        "Cañón San Cristóbal (Barranquitas)",
        "Bosque Seco de Guánica - Flora Tropical",
        "Laguna Tortuguero (Vega Baja) - Refugio Silvestre",
        "Puente Colgante de Caguas - Perspectiva",
        "Atardecer en Crash Boat (Aguadilla)",
        "Composición Macro: Gotas de Rocío en Helechos",
        "Larga Exposición: Caída de Agua El Yunque",
        "Guía de Lente Gran Angular para Paisajes PR",
        "Paleta Cinematográfica: Tonos Cálidos de Isla",
        "Fotografía Aérea: Costas Kársticas del Norte",
        "Sesión Dorada en Playa Domes (Rincón)"
      ],
      descriptions: [
        "Expedición fotográfica y captura de luz natural en paisajes icónicos de Puerto Rico.",
        "Registro visual de texturas naturales, salitre y contrastes tropicales.",
        "Estudio de perspectiva y composición para el archivo Nano Aventuras.",
        "Gestión comercial y técnica de eventos de alta fidelidad.",
        "Captura atmosférica de la costa y el relieve montañoso de la isla."
      ]
    },
    memory: {
      titles: [
        "Vaciado Nocturno de 2 Minutos (Cero Rumiar)",
        "Bitácora de Decisiones Inmutables de Vida",
        "Lección de Trading: Cero Operativa Emocional",
        "Estrategia Yoel Sardiñas: Cambio de Tendencia en Hora",
        "Protocolo Preventivo de Cisterna y Generador",
        "Gratitud por la Cocina de Papá & Alimentos del Hogar",
        "Gratitud Nocturna: Momentos de Conexión",
        "Reflexión: Logro del Día y Estado de Flujo",
        "Hito: Consistencia en la Trinidad Matutina",
        "Hito: Consistencia en Hábitos Semanales",
        "Registro de Mantenimiento Toyota Corolla 2020",
        "Notas de Lectura: The ONE Thing & Hábitos Atómicos",
        "Bitácora: Sesión de Arpegios en Guitarra",
        "Registro: Sesión de Nudos con Cuerda 10mm",
        "Observación: Impacto de la Hidratación en Foco",
        "Bitácora de Salidas Fotográficas PR",
        "Lección de Vida: Reducir Fricción en Rutinas",
        "Resumen Semanal de Metas Alcanzadas",
        "Planificación de Descanso y Desconexión",
        "Diálogo Interior de Serenidad y Agradecimiento",
        "Cierre de Semana: Lecciones Aprendidas y Enfoque"
      ],
      descriptions: [
        "Memoria episódica procesada y consolidada en el archivo de vida.",
        "Registro de aprendizaje personal y retroalimentación constructiva.",
        "Bitácora de seguimiento de evolución personal y bienestar."
      ]
    },
    prod: {
      titles: [
        "Mostrador Home Depot 6405: Servicio & Materiales",
        "Estación de Trabajo: HP Omen 15 + Monitor 32 Omen",
        "Flujo de Revelado en Adobe Lightroom Classic",
        "Compresión JPEGmini Pro & Edición DaVinci",
        "La Única Acción Siguiente (The ONE Thing)",
        "Bloque de Foco Profundo (Deep Work 90m)",
        "Modo Enfoque Blindado: Cero Interrupciones Telefónicas",
        "Timer de 90m Anti-Sedentarismo y Picoteo",
        "Checklist Matutino: 3 Prioridades Clave",
        "Checkpoints Semanales de Acción sin Tareas Huérfanas",
        "Gestión de Archivos en SSD Activo y HDD Archivo",
        "Atajos de Teclado y Flujo de Desarrollo",
        "Automatización de Tareas con Scripts",
        "Minimalismo Digital: Cero Pestañas Inútiles",
        "Modo No Molestar en Bloques Creativos",
        "Organización del Segundo Cerebro en Markdown",
        "Ergonomía Postural y Cuidado de Espalda",
        "Revisión Vespertina de Rendimiento",
        "Gestión Eficiente de Tareas y Checkpoints"
      ],
      descriptions: [
        "Optimización del entorno de trabajo para máxima claridad y fluidez.",
        "Protocolo para sostener concentración profunda sin agotamiento mental.",
        "Alineación de herramientas digitales con los principios de simplicidad."
      ]
    },
    projects: {
      titles: [
        "Casa Inteligente Autosustentable & Energías Híbridas",
        "Circuito Hídrico Cerrado & Filtración de Cisterna",
        "Coworking & Wellness Coffee Shop en Puerto Rico",
        "Plataforma de Salud Holística & Red de Bienestar",
        "Red Comunitaria de Intercambio de Habilidades",
        "Plataforma Integral para Creadores Visuales",
        "Mentoría IA de Transición a la Adultez para Jóvenes",
        "Smart Health Mirror: Biometría & Visagismo",
        "Binoculares Inteligentes con IA para Campo",
        "Detector de Metales & Minerales en PR",
        "Podcast 'Cosas que Nadie me Preguntó' en YouTube",
        "Taller Físico con Prometheus: Sierra de Mesa de Abuelos",
        "Reconstrucción del Fondo de Emergencia con Acciones NYSE",
        "Huerto Soberano: Limones, Pana, Aguacate y Guayaba",
        "Meta Financiera: $3,500/mes de Ingreso Pasivo",
        "Aura Style: Visagismo & Referencias para Barbero",
        "Proyecto: Portafolio Visual Nano Aventuras",
        "Proyecto: Rutina Maestra de Longevidad Zonas Azules",
        "Proyecto: Segundo Cerebro 3D Aura & Prometheus",
        "Proyecto: Repertorio de Música Acústica Criolla",
        "Proyecto: Taller Autónomo Resistente a Huracanes",
        "Proyecto: Automatización del Hogar con Home Assistant",
        "Proyecto: Archivo Fotográfico de Paisajes de PR"
      ],
      descriptions: [
        "Iniciativa a mediano plazo orientada al desarrollo creativo y bienestar.",
        "Estructura de ejecución con metas claras y entregables definidos.",
        "Proyectos de ingeniería y soberanía personal en Puerto Rico."
      ]
    },
    chief: {
      titles: [
        "Auditoría de las 4 C's (Aura Chief of Staff)",
        "Morning Kickoff de Aura a las 8:00 AM",
        "Evening Wind-Down de Descompresión a las 10:00 PM",
        "Alerta Circadiana Anti-Sedentarismo (90 min)",
        "Análisis Semanal de Claridad y Enfoque",
        "Evaluación de Consistencia de Hábitos",
        "Plan de Energía para Días de Alta Carga",
        "Radar de Prioridades Vitales y Metas",
        "Balance Dinámico: Trabajo y Descanso",
        "Guionista Integral del Podcast de YouTube",
        "Co-anfitriona de Voz Ocasional para Debates",
        "Puente de Visión con Prometheus (Taller e Ingeniería)",
        "Onboarding Multi-Tenant & Planes (Free a Business)",
        "Aislamiento Estricto de Datos de la Cuenta Owned"
      ],
      descriptions: [
        "Evaluación ejecutiva de coherencia, capacidad y concentración.",
        "Asistencia estratégica para mantener el rumbo y optimizar recursos cognitivos.",
        "Sistema operativo de Chief of Staff y calibración personalizada."
      ]
    }
  };

  const PROMETHEUS_TOPICS: Record<string, { titles: string[]; descriptions: string[] }> = {
    wiki: {
      titles: [
        "Normas ASCE 7-22: Viento 180 mph en PR",
        "Guía de Corrosión Marina y Salitre Costero",
        "Especificación Acero Inox 316 vs 304",
        "Directorio de Abastos: National Lumber, Home Depot, Ace",
        "Protocolo de Envío Rápido USPS Flat Rate a PR",
        "Hormigón Armado Cat 5: Resistencia y Curado",
        "Seguridad Eléctrica en Ambientes Húmedos Tropicales",
        "Inventario Maestro de Herramientas de Precisión",
        "Diseño Antihuracanes: Techos y Anclajes",
        "Cálculo de Cargas Viento en Cerramientos"
      ],
      descriptions: [
        "Normativa técnica aplicable a estructuras y fabricación en Puerto Rico.",
        "Estándar de resistencia a ambientes marinos de alta salinidad.",
        "Guía de logística y aprovisionamiento local para el taller."
      ]
    },
    cad: {
      titles: [
        "Gabinete Paramétrico 140x90x45mm PETG",
        "Tolerancias de Ensamble y Encaje Deslizante 0.2mm",
        "Inserciones Roscadas de Latón M3 para Cautín",
        "Sello de Hermeticidad O-Ring en TPU 95A",
        "Relleno Giroidal al 40% para Resistencia a Impacto",
        "Montura VESA Reforzada para Pantallas de Taller",
        "Soporte Riel DIN para Fuentes de Poder Conmutadas",
        "Brazo Articulado para Cámara de Inspección",
        "Caja Estanca IP67 con Prensacables PG7",
        "Bisagras Mecánicas Imprimibles sin Soporte",
        "Soporte Antivibración en TPU para Motores",
        "Ensamble Modular de Perfiles de Aluminio 2020",
        "Conducto de Ventilación Forzada con Filtro HEPA",
        "Plantilla de Taladrado para Perfiles Metálicos"
      ],
      descriptions: [
        "Diseño paramétrico optimizado para fabricación aditiva FDM en PETG resistente a UV.",
        "Cálculo de holguras y tolerancias mecánicas para ensambles precisos en taller.",
        "Componente estructural con resistencia a tracción y esfuerzos cortantes."
      ]
    },
    video: {
      titles: [
        "Firmware ESP32: Sensor BME280 Temperatura y Humedad",
        "Transmisión LoRa SX1262 a 915 MHz para Puerto Rico",
        "Control de Motores Paso a Paso con Driver TMC2209",
        "Streaming de Video ESP32-CAM y Detección de Objetos",
        "Modo Deep Sleep y Gestión de Batería de Respaldo",
        "Protocolo ESP-NOW para Comunicación Mesh sin WiFi",
        "Lectura de Shunt INA219 para Medición de Corriente DC",
        "Display OLED I2C SSD1306 con Gráficos en Tiempo Real",
        "Lectura de Temperatura Termocupla MAX6675",
        "Control PID de Temperatura para Cama Caliente",
        "Monitoreo de Voltaje por Celda con ADC de 16 Bits",
        "Integración MQTT con Servidor Local Raspberry Pi"
      ],
      descriptions: [
        "Firmware en C++/Arduino optimizado para bajo consumo y alta estabilidad.",
        "Protocolo de comunicación de largo alcance sin dependencia de red celular.",
        "Controlador embebido con telemetría en tiempo real y protección por watchdog."
      ]
    },
    memory: {
      titles: [
        "Bitácora: Prueba de Carga de Tornillo M3 Inox",
        "Registro: Calibración Extrusor PETG Bambu Lab",
        "Historial: Ensayo de Presión en Tubería CPVC",
        "Ficha Técnica: Adhesivo Epóxico Estructural 3M",
        "Nota de Taller: Reemplazo Llave de Paso 3/4",
        "Bitácora: Mantenimiento de Taladro y Brocas",
        "Registro de Consumo Eléctrico de Impresoras 3D",
        "Historial de Soldaduras con Cautín T12"
      ],
      descriptions: [
        "Bitácora inmutable de pruebas empíricas realizadas en el taller.",
        "Registro de calibraciones mecánicas y térmicas para repetibilidad técnica."
      ]
    },
    solar: {
      titles: [
        "Banco de Baterías 48V (16S LiFePO4 280Ah - 14.3 kWh)",
        "Inversor Híbrido 10kW con Transferencia Automática",
        "Desconectador Bipolar DC 600V con Varistor",
        "Fusible de Protección Class T 300A de Acción Rápida",
        "BMS Daly Smart con Comunicación CAN / RS485",
        "Cálculo de Consumo Crítico de Vivienda y Taller",
        "Monitoreo de Celdas Individuales y Balanceo Activo",
        "Arreglo Solar de Paneles Bifaciales 550W",
        "Protección Contra Sobretensiones Transitorias SPD Tipo 2"
      ],
      descriptions: [
        "Sistema de almacenamiento y respaldo energético para autonomía total ante cortes en PR.",
        "Diseño de microred con protecciones coordinadas y corte seguro por falla de arco."
      ]
    },
    projects: {
      titles: [
        "Proyecto: Reparación de Fuga Slip-Fix 3/4 PVC",
        "Proyecto: Instalación de Calentador en Línea CPVC",
        "Proyecto: Automatización Cisterna Pluvial 2,500 Gal",
        "Proyecto: Microred Fotovoltaica Autónoma PR",
        "Proyecto: Estación Meteorológica Solar LoRa",
        "Proyecto: Banco de Pruebas de Carga para Baterías",
        "Proyecto: Sistema de Riego Automatizado con Electroválvulas",
        "Proyecto: Fresadora CNC de Banco para PCB"
      ],
      descriptions: [
        "Proyecto activo de taller con lista de materiales locales y pasos de ejecución.",
        "Solución de ingeniería práctica adaptada a la infraestructura residencial de PR."
      ]
    },
    agents: {
      titles: [
        "Titán Nikola Tesla (Alta Frecuencia y Control)",
        "Titán Isaac Newton (Física y Resistencia ASCE 7)",
        "Titán Leonardo Da Vinci (Fluidos y Mecanismos)",
        "Titán Elon Musk (Densidad Energética y Escalabilidad)",
        "Titán James Watt (Termodinámica y Eficiencia)",
        "Servidor MCP: Puente Serial y Telemetría en Vivo",
        "Servidor MCP: Automatización de Slicing y GCode",
        "Motor de Debate Técnico para Arbitraje de Diseños"
      ],
      descriptions: [
        "Agente de arbitraje técnico especializado en fundamentos de ingeniería.",
        "Módulo de consulta multi-perspectiva para validación de decisiones críticas."
      ]
    }
  };

  // Generate celestial sphere with hollow interior (void around central nucleus)
  const allNodes = useMemo(() => {
    const nodes: DetailedNode[] = [];

    // Helper: Distribute nodes in a hollow spherical shell around the glowing nucleus
    // All nodes strictly maintain radius R in [0.76, 0.98], keeping the central void (R < 0.72) completely clear!
    const getSphericalShellCoords = (
      catIdx: number,
      totalCats: number,
      itemIdx: number,
      totalInCat: number
    ) => {
      const goldenAngle = 2.399963229728653; // Golden ratio angle

      // 1. Position of category anchor on the unit sphere
      // Latitude catY smoothly distributed from +0.75 down to -0.75
      const catY = totalCats > 1 ? 0.75 - (catIdx / (totalCats - 1)) * 1.5 : 0;
      const catRadiusAtY = Math.sqrt(Math.max(0.02, 1 - catY * catY));
      const catTheta = catIdx * goldenAngle * 2.15;

      const anchorX = Math.cos(catTheta) * catRadiusAtY;
      const anchorY = catY;
      const anchorZ = Math.sin(catTheta) * catRadiusAtY;

      // If it's the anchor itself, place it at shell radius 0.88
      if (itemIdx === 0) {
        const shellR = 0.88;
        return { x: anchorX * shellR, y: anchorY * shellR, z: anchorZ * shellR };
      }

      // 2. Distribute member nodes in an organic celestial cap on the sphere around the anchor
      const localPhi = itemIdx * goldenAngle;
      const spread = Math.min(0.68, 0.16 + Math.sqrt(itemIdx / Math.max(1, totalInCat)) * 0.44);

      // Tangent vector offset on the sphere surface
      const tx = Math.cos(localPhi) * spread;
      const tz = Math.sin(localPhi) * spread;
      const ty = (Math.sin(itemIdx * 1.9) * 0.45) * spread;

      let nx = anchorX + tx;
      let ny = anchorY + ty;
      let nz = anchorZ + tz;

      // Normalize strictly onto unit sphere
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len;
      ny /= len;
      nz /= len;

      // Shell radius strictly in [0.76, 0.98]
      // Central nucleus is at R <= 0.40, creating a clean 0.36 unit hollow buffer cavity
      const shellRadius = 0.84 + Math.sin(itemIdx * 3.3) * 0.08 + ((itemIdx % 5) * 0.02);

      return {
        x: nx * shellRadius,
        y: ny * shellRadius,
        z: nz * shellRadius,
      };
    };

    if (domain === "prometheus") {
      // 1. Prometheus Independent Engineering Galaxy (188 real technical nodes on spherical shell)
      const anchors: DetailedNode[] = [
        {
          id: "p_anchor_wiki",
          title: "Normas ASCE 7-22 & PR",
          sourceId: "wiki",
          sourceName: "Normas ASCE 7-22 & PR",
          color: "#38bdf8",
          ...getSphericalShellCoords(0, 7, 0, 18),
          cluster: "Wiki",
          isKeyNode: true,
          filename: "brain/PROMETHEUS.md",
          description: "Contexto maestro: normas ASCE 7-22 (175-185 mph), salitre, Acero Inox 316 y abastos en Puerto Rico.",
          content: `# PROMETHEUS.md - Contexto Maestro (C1: Context)\n\n## 1. Identidad\n* Prometeo (español) / Prometheus (inglés). Maestro creador y tutor de ingeniería aplicada.\n\n## 2. Reglas Inquebrantables\n* Prioridad absoluta al inventario real en mano antes de comprar.\n* Cero datos simulados o ficticios.\n* Salidas ejecutables con 3 cajas: Starter Kit, Acción y Criterio.\n\n## 3. Filtro Geográfico Puerto Rico\n* Vientos huracanados ASCE 7: 175 a 185 mph (Cat 5).\n* Tornillería Acero Inoxidable grado 316.\n* Abastos: Home Depot PR, National Lumber, Ferreterías Ace, USPS Priority.`,
          connections: ["p_anchor_memory", "p_anchor_cad", "p_anchor_solar", "p_anchor_projects"]
        },
        {
          id: "p_anchor_cad",
          title: "Diseño CAD & Sólidos 3D",
          sourceId: "cad",
          sourceName: "Diseño CAD & Sólidos 3D",
          color: "#fb7185",
          ...getSphericalShellCoords(1, 7, 0, 42),
          cluster: "CAD",
          isKeyNode: true,
          filename: "brain/proyectos/gabinete_parametrico.md",
          description: "Ensamble paramétrico para electrónica (140x90x45mm) en PETG con sellos TPU y tornillos M3 inox.",
          content: `# Proyecto: Gabinete Paramétrico Electrónico\n\n* Dimensiones: 140 mm largo x 90 mm ancho x 45 mm alto.\n* Material: PETG resistente a rayos UV y calor de PR.\n* Fijación: Inserciones roscadas de latón M3 para cautín.\n* Slicing: Relleno giroidal al 40% para rigidez multidireccional.`,
          connections: ["p_anchor_wiki", "p_anchor_memory"]
        },
        {
          id: "p_anchor_firmware",
          title: "Firmware ESP32 & LoRa 915MHz",
          sourceId: "firmware",
          sourceName: "Firmware ESP32 & LoRa 915MHz",
          color: "#ec4899",
          ...getSphericalShellCoords(2, 7, 0, 35),
          cluster: "Firmware",
          isKeyNode: true,
          filename: "brain/firmware/esp32_lora_mesh.md",
          description: "Telemetría LoRa 915 MHz, streaming ESP32-CAM y protocolo de control de motores.",
          content: `# Firmware ESP32 & Video Telemetría\n\n* Frecuencia: 915 MHz para banda ISM en Puerto Rico.\n* Sensor: BME280 sobre bus I2C con lectura cada 30 segundos.\n* Respaldo: Batería LiFePO4 con modo deep-sleep de 15uA.`,
          connections: ["p_anchor_wiki", "p_anchor_memory"]
        },
        {
          id: "p_anchor_memory",
          title: "Bitácora & Ensayos MOC",
          sourceId: "memory",
          sourceName: "Bitácora & Ensayos MOC",
          color: "#f59e0b",
          ...getSphericalShellCoords(3, 7, 0, 26),
          cluster: "Memoria",
          isKeyNode: true,
          filename: "brain/INDEX.md",
          description: "Mapa de Contenido (MOC) central estilo Wikipedia para navegar sin ruido.",
          content: `# INDEX.md - Mapa de Contenido de Taller\n\n* Proyectos: Gabinete paramétrico, Residencia Ecoautosustentable, Plomería/Calentador.\n* Perfiles: Herramientas en taller, clima de PR.\n* Decisiones: Bitácora inmutable de opciones de diseño.`,
          connections: ["p_anchor_wiki", "p_anchor_cad", "p_anchor_solar", "p_anchor_projects"]
        },
        {
          id: "p_anchor_solar",
          title: "Microred 48V LiFePO4",
          sourceId: "solar",
          sourceName: "Microred 48V LiFePO4",
          color: "#00e5ff",
          ...getSphericalShellCoords(4, 7, 0, 24),
          cluster: "Solar",
          isKeyNode: true,
          filename: "brain/proyectos/casa_ecoautosustentable.md",
          description: "Hormigón armado Cat 5 (175+ mph), microgrid 48V LiFePO4 (14.3 kWh) y cisterna pluvial 2,500 gal.",
          content: `# Proyecto: Casa Ecoautosustentable en Puerto Rico\n\n* Estructura: Bloques de 8" rellenos con varilla #4 a 16" O.C., losa 4".\n* Banco de Baterías: 48V nominales (16S LiFePO4 280Ah = 14.3 kWh).\n* Inversor: Híbrido 10 kW con desconectador bipolar DC y fusible Class T 300A.\n* Agua: Cisterna pluvial 2,500 galones con bomba inverter presurizada.`,
          connections: ["p_anchor_wiki", "p_anchor_memory"]
        },
        {
          id: "p_anchor_projects",
          title: "Proyectos de Taller",
          sourceId: "projects",
          sourceName: "Proyectos de Taller",
          color: "#10b981",
          ...getSphericalShellCoords(5, 7, 0, 31),
          cluster: "Proyectos",
          isKeyNode: true,
          filename: "brain/proyectos/reparacion_plomeria.md",
          description: "Procedimiento de corte y unión Slip-Fix 3/4 PVC, calentador en línea CPVC y mezcladora 8 pulg.",
          content: `# Proyecto: Plomería & Calentador de Agua\n\n* Fuga 3/4": Unión deslizante Slip-Fix con cemento solvente gris de fraguado rápido.\n* Calentador: Tubería CPVC o PEX-A (nunca PVC blanco para agua caliente).\n* Mezcladora: Tomas a 8" centros, teflón alta densidad + pipe dope.`,
          connections: ["p_anchor_wiki", "p_anchor_memory"]
        },
        {
          id: "p_anchor_titans",
          title: "Consejo de Titanes",
          sourceId: "titans",
          sourceName: "Consejo de Titanes",
          color: "#a855f7",
          ...getSphericalShellCoords(6, 7, 0, 12),
          cluster: "Titanes",
          isKeyNode: true,
          filename: "src/components/prometheus/TitansCouncilStudio.tsx",
          description: "Debate técnico dinámico: Tesla (Firmware/Control), Newton (Física ASCE 7), Da Vinci (Fluidos), Musk (Litio).",
          content: `Debate dinámico multi-perspectiva para arbitraje de proyectos complejos de ingeniería.`,
          connections: ["p_anchor_wiki"]
        }
      ];

      nodes.push(...anchors);

      // Synthesize remaining Prometheus member nodes (distributed along spherical shell)
      prometheusSources.forEach((src, catIdx) => {
        const remainingInSource = src.count - anchors.filter(a => a.sourceId === src.id).length;
        const pool = PROMETHEUS_TOPICS[src.id] || { titles: [src.name], descriptions: [`Elemento en ${src.name}`] };

        for (let i = 0; i < remainingInSource; i++) {
          const coords = getSphericalShellCoords(catIdx, prometheusSources.length, i + 1, remainingInSource);
          const baseTitle = pool.titles[i % pool.titles.length];
          const cycle = Math.floor(i / pool.titles.length) + 1;
          const title = cycle > 1 ? `${baseTitle} #${cycle}` : baseTitle;
          const desc = pool.descriptions[i % pool.descriptions.length];
          const parentAnchor = anchors.find(a => a.sourceId === src.id) || anchors[0];

          const itemConnections: string[] = [parentAnchor.id];
          if (i > 0) {
            itemConnections.push(`p_syn_${src.id}_${i - 1}`);
          }
          if (i % 3 === 0 && i > 2) {
            itemConnections.push(`p_syn_${src.id}_${i - 2}`);
          }

          nodes.push({
            id: `p_syn_${src.id}_${i}`,
            title,
            sourceId: src.id,
            sourceName: src.name,
            color: src.color,
            x: coords.x,
            y: coords.y,
            z: coords.z,
            cluster: src.name,
            isKeyNode: false,
            needsAttention: i % 18 === 0,
            filename: `brain/${src.id}/item_${i + 1}.md`,
            description: desc,
            content: `# ${title}\n\n* Dominio: Ingeniería y Taller Prometheus\n* Categoría: ${src.name}\n* Especificación: Validado bajo condiciones climáticas y estándares de construcción de Puerto Rico.`,
            connections: itemConnections
          });
        }
      });
    } else {
      // 2. AURA DOMAIN: Hybrid Fusion (7 Master Pillars + Curated Galaxy + Real User Live Memories)
      const auraAnchors: DetailedNode[] = [
        {
          id: "a_anchor_values",
          title: "AURA.md (Valores & Vida)",
          sourceId: "values",
          sourceName: "Wiki Personal & Valores",
          color: "#38bdf8",
          ...getSphericalShellCoords(0, 7, 0, 12),
          cluster: "Valores",
          isKeyNode: true,
          filename: "aura_brain/AURA.md",
          description: "Contexto maestro: Chief of Staff, brújula moral estoica (Marco Aurelio), biohacking 100+ años, sueño 7.5-8h y protocolo de descompresión.",
          content: `# AURA.md - Contexto Maestro de Vida (C1: Context)\n\n## 1. Identidad\n* Aura: Chief of Staff personal y guardiana de energía vital de Angel.\n\n## 2. Brújula Moral & Principios\n* Paz mental antes que hiper-estimulación.\n* Meditaciones de Marco Aurelio y amor fati.\n* Cero rumiar mental: 'Toma agua, respira hondo y descansa'.\n* Longevidad 100+ años: masa muscular, densidad ósea y comida 100% real.`,
          connections: ["a_anchor_memory", "a_anchor_habits"]
        },
        {
          id: "a_anchor_habits",
          title: "Trinidad Matutina & Hábitos",
          sourceId: "habits",
          sourceName: "Hábitos & Bienestar",
          color: "#fb7185",
          ...getSphericalShellCoords(1, 7, 0, 24),
          cluster: "Hábitos",
          isKeyNode: true,
          filename: "aura_brain/perfil/habitos_rutinas.md",
          description: "Trinidad Matutina, nudos de escalada y estiba, guitarra Yamaha acústica, calistenia, co-working con Christian y pistas deportivas.",
          content: `# Registro de Hábitos & Rutinas\n\n* Trinidad Matutina: Respiración consciente -> Mantras -> Desayuno propio.\n* Nudos marineros y escalada: As de Guía, Ballestrinque, Ocho Doble.\n* Co-working con Christian en Starbucks Dorado/Guaynabo.\n* Entrenamiento en pistas de Comerío, Cidra y Bayamón.`,
          connections: ["a_anchor_values", "a_anchor_memory"]
        },
        {
          id: "a_anchor_nano",
          title: "Nano Aventuras & Fotografía PR",
          sourceId: "nano",
          sourceName: "Nano Aventuras (Fotos PR)",
          color: "#ec4899",
          ...getSphericalShellCoords(2, 7, 0, 16),
          cluster: "Fotografía",
          isKeyNode: true,
          filename: "aura_brain/proyectos/nano_aventuras.md",
          description: "Expediciones por Puerto Rico (Cueva Ventana, Mar Chiquita, Río Abajo, Cabo Rojo), Sony A7 III, Drone Air 3 y eventos con ATH Móvil.",
          content: `# Proyecto Creativo: Nano Aventuras\n\n* Rutas: Cueva Ventana, Mar Chiquita, Río Abajo y Cabo Rojo.\n* Arsenal: Sony A7 III, DJI Air 3, RS 3, Lark Max, Selphy 1500.\n* Eventos: Bodas y quinceañeros con depósitos ATH Móvil.\n* Podcast: 'Cosas que Nadie me Preguntó'.`,
          connections: ["a_anchor_values", "a_anchor_projects"]
        },
        {
          id: "a_anchor_memory",
          title: "INDEX.md (Mapa de Vida)",
          sourceId: "memory",
          sourceName: "Memoria de Vida & Diario",
          color: "#f59e0b",
          ...getSphericalShellCoords(3, 7, 0, 20),
          cluster: "Memoria",
          isKeyNode: true,
          filename: "aura_brain/INDEX.md",
          description: "Mapa de Contenido (MOC) de vida: biometría, cognición, bitácora de nudos y guitarra, huerto y bitácora de decisiones inmutables.",
          content: `# INDEX.md - Mapa de Vida & Bienestar\n\n* Directorio maestro del segundo cerebro de Angel.\n* Registro de hábitos, fotografía, salud y decisiones inmutables (decisiones/log.md).`,
          connections: ["a_anchor_values", "a_anchor_habits"]
        },
        {
          id: "a_anchor_prod",
          title: "Productividad & HD 6405",
          sourceId: "prod",
          sourceName: "Productividad & HD 6405",
          color: "#00e5ff",
          ...getSphericalShellCoords(4, 7, 0, 15),
          cluster: "Productividad",
          isKeyNode: true,
          filename: "aura_brain/proyectos/productividad_personal.md",
          description: "Home Depot 6405, estación HP Omen 15 + monitor 32 Omen, Lightroom Classic, atajos/scripts y estrategia de trading Cambio de Tendencia en Hora.",
          content: `# Productividad Personal & Estación HD 6405\n\n* Puesto HD 6405 y estación HP Omen 15 + monitor 32 Omen.\n* Flujo digital: Lightroom Classic, DaVinci, Audacity, scripts y Markdown.\n* Trading: 'Cambio de Tendencia en Hora' (Yoel Sardiñas), cero impulsividad.`,
          connections: ["a_anchor_values"]
        },
        {
          id: "a_anchor_projects",
          title: "Proyectos de Vida & Innovación",
          sourceId: "projects",
          sourceName: "Proyectos de Vida",
          color: "#10b981",
          ...getSphericalShellCoords(5, 7, 0, 14),
          cluster: "Proyectos",
          isKeyNode: true,
          filename: "aura_brain/proyectos/banco_ideas_proyectos.md",
          description: "Banco de innovación (16 proyectos), portafolio creativo visual, taller físico con Prometheus y huerto soberano.",
          content: `# Bóveda Maestra de Ideas de Innovación & Proyectos de Vida\n\n* 16 proyectos clasificados en Hogar Sustentable, Bienestar, Hardware IA y Medios.\n* Portafolio visual Nano Aventuras y taller físico con Prometheus.`,
          connections: ["a_anchor_values", "a_anchor_nano"]
        },
        {
          id: "a_anchor_chief",
          title: "Aura OS & Chief of Staff",
          sourceId: "chief",
          sourceName: "Agentes & Asistente Ejecutivo",
          color: "#a855f7",
          ...getSphericalShellCoords(6, 7, 0, 8),
          cluster: "Chief of Staff",
          isKeyNode: true,
          filename: "aura_brain/proyectos/onboarding_arquitectura_usuarios.md",
          description: "Auditoría de 4 C's, Morning Kickoff, Evening Wind-Down, onboarding multi-tenant y aislamiento estricto de cuenta Owned.",
          content: `# Aura Chief of Staff & Onboarding Multi-Tenant\n\n* Auditoría de las 4 C's, briefings diarios y protocolo de descompresión.\n* Aislamiento radical RLS para la cuenta personal de Angel.\n* Modelo de 4 niveles comerciales con conversión escalonada.`,
          connections: ["a_anchor_values"]
        }
      ];

      nodes.push(...auraAnchors);

      // 1. Generate full curated second brain galaxy (109 topic nodes)
      BASE_AURA_SOURCES.forEach((src, catIdx) => {
        const anchor = auraAnchors.find((a) => a.sourceId === src.id) || auraAnchors[0];
        const pool = AURA_TOPICS[src.id] || { titles: [src.name], descriptions: [`Elemento en ${src.name}`] };
        const baseItemCount = pool.titles.length;

        for (let i = 0; i < baseItemCount; i++) {
          const coords = getSphericalShellCoords(catIdx, BASE_AURA_SOURCES.length, i + 1, baseItemCount);
          const baseTitle = pool.titles[i % pool.titles.length];
          const cycle = Math.floor(i / pool.titles.length) + 1;
          const title = cycle > 1 ? `${baseTitle} #${cycle}` : baseTitle;
          const desc = pool.descriptions[i % pool.descriptions.length];

          const itemConnections: string[] = [anchor.id];
          if (i > 0) {
            itemConnections.push(`a_syn_${src.id}_${i - 1}`);
          }

          nodes.push({
            id: `a_syn_${src.id}_${i}`,
            title,
            sourceId: src.id,
            sourceName: src.name,
            color: src.color,
            x: coords.x,
            y: coords.y,
            z: coords.z,
            cluster: src.name,
            isKeyNode: false,
            needsAttention: i % 18 === 0,
            filename: `aura_brain/${src.id}/note_${i + 1}.md`,
            description: desc,
            content: `# ${title}\n\n* Dominio: Vida Personal y Bienestar Aura\n* Categoría: ${src.name}\n* Registro: ${desc}`,
            connections: itemConnections,
          });
        }
      });

      // 2. Augment organically with Real Live Memories from SQLite/PostgreSQL
      if (liveData?.nodes && liveData.nodes.length > 0) {
        const liveBySource: Record<string, typeof liveData.nodes> = {};
        liveData.nodes.forEach((n) => {
          const sId = mapCategoryToAuraSource(n.category || "");
          if (!liveBySource[sId]) liveBySource[sId] = [];
          liveBySource[sId].push(n);
        });

        BASE_AURA_SOURCES.forEach((src, catIdx) => {
          const items = liveBySource[src.id] || [];
          const parentAnchor = auraAnchors.find((a) => a.sourceId === src.id) || auraAnchors[0];

          items.forEach((item, itemIdx) => {
            const coords = getSphericalShellCoords(
              catIdx,
              BASE_AURA_SOURCES.length,
              (AURA_TOPICS[src.id]?.titles.length || 10) + itemIdx + 1,
              (AURA_TOPICS[src.id]?.titles.length || 10) + items.length
            );

            const titleText =
              item.title ||
              item.label ||
              (item.content ? item.content.slice(0, 48) + (item.content.length > 48 ? "..." : "") : `Recuerdo #${itemIdx + 1}`);

            const edges = liveData.edges || [];
            const itemConnections = edges
              .filter((e) => e.source === item.id || e.target === item.id)
              .map((e) => (e.source === item.id ? e.target : e.source));

            if (itemConnections.length === 0) {
              itemConnections.push(parentAnchor.id);
            }

            nodes.push({
              id: `live_${item.id}`,
              title: titleText,
              sourceId: src.id,
              sourceName: src.name,
              color: src.color,
              x: coords.x,
              y: coords.y,
              z: coords.z,
              cluster: src.name,
              isKeyNode: false,
              isLiveMemory: true,
              created_at: item.created_at,
              filename: `memoria/${src.id}/${item.id.slice(0, 8)}.md`,
              description: item.content ? item.content.slice(0, 110) : `Recuerdo en vivo en ${src.name}`,
              content: item.content || `# ${titleText}\n\n* Categoría: ${src.name}\n* Fecha: ${item.created_at || "Reciente"}`,
              connections: itemConnections,
            });
          });
        });
      }
    }

    // Host-injected workshop / brain notes (Prometheus save-to-brain, etc.)
    if (extraNodes && extraNodes.length > 0) {
      extraNodes.forEach((sn, sIdx) => {
        const coords = getSphericalShellCoords(
          domain === "prometheus" ? 5 : 3,
          currentSources.length || 7,
          sIdx + 15,
          extraNodes.length + 15
        );
        nodes.push({
          ...sn,
          id: sn.id || `extra_${sIdx}`,
          title: sn.title || "Nota de taller",
          sourceId: sn.sourceId || (domain === "prometheus" ? "projects" : "memory"),
          sourceName: sn.sourceName || (domain === "prometheus" ? "Proyectos de Taller" : "Memoria de Vida"),
          color: sn.color || "#10b981",
          x: sn.x ?? coords.x,
          y: sn.y ?? coords.y,
          z: sn.z ?? coords.z,
          cluster: sn.cluster || sn.sourceName || "Notas",
          isKeyNode: sn.isKeyNode ?? true,
          isLiveSaved: sn.isLiveSaved ?? true,
          filename: sn.filename || `brain/note_${sIdx + 1}.md`,
          description: sn.description || "Ficha técnica guardada desde el taller.",
          content: sn.content || `# ${sn.title || "Nota"}\n\nDiagnóstico / nota guardada.`,
          connections: sn.connections || [],
        });
      });
    }

    return nodes;
  }, [domain, prometheusSources, auraSources, liveData, extraNodes, currentSources.length]);

  // Filter nodes based on active sources, search query, and "needs attention"
  const activeSourceIds = useMemo(() => {
    return new Set(currentSources.filter((s) => s.active).map((s) => s.id));
  }, [currentSources]);

  const visibleNodes = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return allNodes.filter((n) => {
      if (!activeSourceIds.has(n.sourceId)) return false;
      if (onlyNeedsAttention && !n.needsAttention) return false;
      if (q) {
        return (
          n.title.toLowerCase().includes(q) ||
          n.description.toLowerCase().includes(q) ||
          n.cluster.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allNodes, activeSourceIds, onlyNeedsAttention, searchQuery]);

  // Dynamic calculation of unique synaptic connections
  const totalConnections = useMemo(() => {
    const edgeSet = new Set<string>();
    visibleNodes.forEach((n) => {
      n.connections?.forEach((targetId) => {
        const pair = [n.id, targetId].sort().join("---");
        edgeSet.add(pair);
      });
    });
    return edgeSet.size || (domain === "prometheus" ? 460 : 107);
  }, [visibleNodes, domain]);

  // Auto-open key node once per domain only — never re-force after user closes
  const autoOpenedForDomainRef = useRef<BrainDomain | null>(null);
  const userClosedPanelRef = useRef(false);
  useEffect(() => {
    if (allNodes.length === 0) return;
    if (autoOpenedForDomainRef.current === domain) return;
    autoOpenedForDomainRef.current = domain;
    userClosedPanelRef.current = false;
    const defaultNode = allNodes.find((n) => n.isKeyNode) || allNodes[0];
    setSelectedNode(defaultNode || null);
    if (defaultNode) setEditedMarkdown(defaultNode.content);
  }, [domain, allNodes]);

  useEffect(() => {
    if (selectedNode) {
      setEditedMarkdown(selectedNode.content);
      setIsEditingContent(false);
    }
  }, [selectedNode]);

  // Dynamic Canvas Resize Observer for retina-sharp display without distortion
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          const dpr = window.devicePixelRatio || 1;
          canvas.width = Math.floor(width * dpr);
          canvas.height = Math.floor(height * dpr);
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // 3D Projection Matrix Helper
  const project3D = useCallback((
    x: number, y: number, z: number,
    cx: number, cy: number, baseRadius: number,
    cosY: number, sinY: number, cosX: number, sinX: number
  ) => {
    const x1 = x * cosY - z * sinY;
    const z1 = z * cosY + x * sinY;
    const y2 = y * cosX - z1 * sinX;
    const z2 = z1 * cosX + y * sinX;

    const cameraDistance = 2.6;
    const perspective = cameraDistance / (cameraDistance - z2);
    const screenX = cx + x1 * baseRadius * perspective;
    const screenY = cy - y2 * baseRadius * perspective;

    return { screenX, screenY, depth: z2, perspective };
  }, []);

  // Helper to find closest node under mouse pointer
  const findNodeAtPosition = useCallback((
    mouseX: number, mouseY: number, maxDist = 24
  ): DetailedNode | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;

    const cx = width / 2 + panOffsetRef.current.x;
    const cy = height / 2 + panOffsetRef.current.y;
    const baseRadius = Math.min(width, height) * 0.42 * zoomRef.current;

    const { rotX, rotY } = rotationRef.current;
    const cosY = Math.cos(rotY);
    const sinY = Math.sin(rotY);
    const cosX = Math.cos(rotX);
    const sinX = Math.sin(rotX);

    let closestNode: DetailedNode | null = null;
    let minDistance = maxDist;

    for (const n of visibleNodes) {
      const p = project3D(n.x, n.y, n.z, cx, cy, baseRadius, cosY, sinY, cosX, sinX);
      const d = Math.hypot(mouseX - p.screenX, mouseY - p.screenY);
      if (d < minDistance) {
        minDistance = d;
        closestNode = n;
      }
    }

    return closestNode;
  }, [visibleNodes, project3D]);

  // Main 3D Rendering Loop (Herk Brain exact aesthetics)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const render = () => {
      time += 0.02;

      // Handle Inertia and Auto-Rotation
      if (isPlayingDemo) {
        demoProgressRef.current += 0.007;
        rotationRef.current.rotY = Math.sin(demoProgressRef.current) * 1.6;
        rotationRef.current.rotX = 0.22 + Math.cos(demoProgressRef.current * 0.65) * 0.28;
        zoomRef.current = 1.0 + Math.sin(demoProgressRef.current * 1.1) * 0.25;
      } else if (isCinemaMode && !isDraggingRef.current) {
        rotationRef.current.rotY += 0.0022;
      } else if (!isDraggingRef.current) {
        // Apply inertia decay
        rotationRef.current.rotY += inertiaRef.current.vx;
        rotationRef.current.rotX = Math.max(-1.45, Math.min(1.45, rotationRef.current.rotX + inertiaRef.current.vy));
        inertiaRef.current.vx *= 0.92;
        inertiaRef.current.vy *= 0.92;
      }

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // Deep space subtle gradient background
      const spaceGrad = ctx.createRadialGradient(width / 2, height / 2, 40, width / 2, height / 2, Math.max(width, height) * 0.7);
      spaceGrad.addColorStop(0, domain === "prometheus" ? "#0a1226" : "#130924");
      spaceGrad.addColorStop(0.65, "#060914");
      spaceGrad.addColorStop(1, "#03050b");
      ctx.fillStyle = spaceGrad;
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2 + panOffsetRef.current.x;
      const cy = height / 2 + panOffsetRef.current.y;
      const baseRadius = Math.min(width, height) * 0.42 * zoomRef.current;

      const { rotX, rotY } = rotationRef.current;
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);

      // 1. Draw 3 Large Tilted Orbital Rings (Planetary ellipses in Herk Brain)
      ctx.save();
      const ringPlanes = [
        { tiltX: 0.35, tiltZ: 0.85, color: domain === "prometheus" ? "rgba(56, 189, 248, 0.16)" : "rgba(168, 85, 247, 0.18)" },
        { tiltX: -0.55, tiltZ: 0.45, color: domain === "prometheus" ? "rgba(245, 158, 11, 0.13)" : "rgba(244, 63, 94, 0.14)" },
        { tiltX: 0.08, tiltZ: -0.92, color: "rgba(255, 255, 255, 0.08)" },
      ];

      ringPlanes.forEach((rp) => {
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2 + 0.1; a += 0.07) {
          const rx = Math.cos(a) * 1.18;
          const rz = Math.sin(a) * 1.18;
          const ry = rx * rp.tiltX + rz * rp.tiltZ * 0.32;
          const p = project3D(rx, ry, rz, cx, cy, baseRadius, cosY, sinY, cosX, sinX);
          if (a === 0) ctx.moveTo(p.screenX, p.screenY);
          else ctx.lineTo(p.screenX, p.screenY);
        }
        ctx.strokeStyle = rp.color;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      });
      ctx.restore();

      // 2. Draw Central Nucleus Geodesic Icosahedron Core (The glowing cage in Herk Brain)
      ctx.save();
      const coreSize = 0.24 * (1 + Math.sin(time * 2.2) * 0.04);
      const coreVerts = [
        [0, 1, 0], [0, -1, 0],
        [0.89, 0.45, 0], [-0.89, 0.45, 0], [0.89, -0.45, 0], [-0.89, -0.45, 0],
        [0, 0.45, 0.89], [0, 0.45, -0.89], [0, -0.45, 0.89], [0, -0.45, -0.89]
      ];

      const projCoreVerts = coreVerts.map((v) =>
        project3D(v[0] * coreSize, v[1] * coreSize, v[2] * coreSize, cx, cy, baseRadius, cosY, sinY, cosX, sinX)
      );

      ctx.strokeStyle = domain === "prometheus" ? "rgba(56, 189, 248, 0.4)" : "rgba(168, 85, 247, 0.45)";
      ctx.lineWidth = 1.2;
      for (let i = 0; i < projCoreVerts.length; i++) {
        for (let j = i + 1; j < projCoreVerts.length; j++) {
          const d = Math.hypot(coreVerts[i][0] - coreVerts[j][0], coreVerts[i][1] - coreVerts[j][1], coreVerts[i][2] - coreVerts[j][2]);
          if (d < 1.3) {
            ctx.beginPath();
            ctx.moveTo(projCoreVerts[i].screenX, projCoreVerts[i].screenY);
            ctx.lineTo(projCoreVerts[j].screenX, projCoreVerts[j].screenY);
            ctx.stroke();
          }
        }
      }

      // Inner Glowing Nucleus Core
      const centerProj = project3D(0, 0, 0, cx, cy, baseRadius, cosY, sinY, cosX, sinX);
      const coreGrad = ctx.createRadialGradient(centerProj.screenX, centerProj.screenY, 2, centerProj.screenX, centerProj.screenY, 36);
      coreGrad.addColorStop(0, "#ffffff");
      coreGrad.addColorStop(0.3, domain === "prometheus" ? "rgba(56, 189, 248, 0.85)" : "rgba(217, 70, 239, 0.85)");
      coreGrad.addColorStop(0.7, domain === "prometheus" ? "rgba(14, 165, 233, 0.3)" : "rgba(168, 85, 247, 0.3)");
      coreGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(centerProj.screenX, centerProj.screenY, 36, 0, Math.PI * 2);
      ctx.fill();

      // Central Core Floating Badge / Avatar (identical to "Claude Code" in Herk Brain)
      const coreTitle = domain === "prometheus" ? "Prometheus Core" : "Aura Core";
      ctx.font = "600 11px Inter, system-ui, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.shadowColor = "#000000";
      ctx.shadowBlur = 6;
      ctx.fillText(coreTitle, centerProj.screenX, centerProj.screenY - 26);
      ctx.shadowBlur = 0;

      // Small central square badge (like the green avatar in Herk's screenshot)
      ctx.fillStyle = domain === "aura" ? "#a855f7" : "#00e5ff";
      ctx.beginPath();
      ctx.roundRect(centerProj.screenX - 8, centerProj.screenY - 8, 16, 16, 4);
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // 3. Project Nodes and Sort by Depth
      const projected = visibleNodes.map((n) => {
        const p = project3D(n.x, n.y, n.z, cx, cy, baseRadius, cosY, sinY, cosX, sinX);
        return {
          ...n,
          ...p,
          isSelected: selectedNode?.id === n.id,
          isHovered: hoveredNode?.id === n.id,
        };
      });

      projected.sort((a, b) => a.depth - b.depth);

      // 4. Draw Synaptic Lines between connected nodes (surface mesh on spherical shell)
      ctx.save();
      const nodeLookup = new Map(projected.map((n) => [n.id, n]));
      const drawnPairs = new Set<string>();

      projected.forEach((node) => {
        if (!node.connections || node.connections.length === 0) return;
        node.connections.forEach((connId) => {
          const target = nodeLookup.get(connId);
          if (!target) return;

          const pairKey = node.id < target.id ? `${node.id}-${target.id}` : `${target.id}-${node.id}`;
          if (drawnPairs.has(pairKey)) return;
          drawnPairs.add(pairKey);

          // Keep central cavity hollow: only draw surface mesh connections (never pass through center nucleus)
          const dist3D = Math.hypot(node.x - target.x, node.y - target.y, node.z - target.z);
          if (dist3D > 1.35) return;

          const isHighlighted = node.isSelected || target.isSelected || node.isHovered || target.isHovered;
          ctx.beginPath();
          ctx.moveTo(node.screenX, node.screenY);
          ctx.lineTo(target.screenX, target.screenY);

          ctx.strokeStyle = isHighlighted
            ? domain === "prometheus" ? "rgba(56, 189, 248, 0.85)" : "rgba(244, 63, 94, 0.85)"
            : "rgba(148, 163, 184, 0.16)";
          ctx.lineWidth = isHighlighted ? 2.2 : 0.8;
          ctx.stroke();

          // Animated bright pulse particle traveling along line
          if (isHighlighted) {
            const t = (Math.sin(time * 3.5 + node.x * 3) + 1) / 2;
            const px = node.screenX + (target.screenX - node.screenX) * t;
            const py = node.screenY + (target.screenY - node.screenY) * t;
            ctx.beginPath();
            ctx.arc(px, py, 3, 0, Math.PI * 2);
            ctx.fillStyle = "#ffffff";
            ctx.shadowColor = domain === "prometheus" ? "#38bdf8" : "#f43f5e";
            ctx.shadowBlur = 9;
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        });
      });
      ctx.restore();

      // 5. Draw Particle Nodes & Floating Labels
      projected.forEach((n) => {
        const baseSize = n.isKeyNode ? 4.5 : 2.2;
        const radius = (n.isSelected || n.isHovered ? 8 : baseSize) * n.perspective;

        ctx.save();

        // Outer glow halo
        if (n.isSelected || n.isHovered || n.isKeyNode) {
          ctx.beginPath();
          ctx.arc(n.screenX, n.screenY, radius * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = n.color;
          ctx.globalAlpha = n.isSelected || n.isHovered ? 0.45 : 0.2;
          ctx.fill();
        }

        // Main Node Particle
        ctx.beginPath();
        ctx.arc(n.screenX, n.screenY, Math.max(1.4, radius), 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.globalAlpha = n.isSelected || n.isHovered ? 1.0 : 0.85;
        ctx.shadowColor = n.color;
        ctx.shadowBlur = n.isSelected || n.isHovered ? 16 : 4;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Selection Pulse Ring
        if (n.isSelected) {
          const pulseR = radius * (1.6 + 0.3 * Math.sin(time * 4));
          ctx.beginPath();
          ctx.arc(n.screenX, n.screenY, pulseR, 0, Math.PI * 2);
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        // Live Memory / Saved workshop Celestial Beacon Ring
        if (n.isLiveMemory || n.isLiveSaved) {
          const livePulse = radius * (1.4 + 0.3 * Math.sin(time * 3.5 + (n.x * 4)));
          ctx.beginPath();
          ctx.arc(n.screenX, n.screenY, livePulse, 0, Math.PI * 2);
          ctx.strokeStyle = n.isLiveSaved ? "#10b981" : "#38bdf8";
          ctx.lineWidth = 1.2;
          ctx.globalAlpha = 0.8;
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }

        // Attention Beacon Ring (Amber pulse for neglected habits/projects >7 days)
        if (n.needsAttention && !n.isSelected) {
          const attnPulse = radius * (1.5 + 0.35 * Math.sin(time * 4.5 + (n.y * 3)));
          ctx.beginPath();
          ctx.arc(n.screenX, n.screenY, attnPulse, 0, Math.PI * 2);
          ctx.strokeStyle = "#f59e0b"; // Warm Amber/Orange
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = 0.85;
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }

        // Draw Floating Text Labels (Key anchor hubs, hovered node, or showAllLabels)
        if (n.isKeyNode) {
          // Elegant category anchor capsule badge (only 7 across the universe)
          ctx.font = "600 10.5px Inter, system-ui, sans-serif";
          const textWidth = ctx.measureText(n.title).width;
          const badgeW = textWidth + 22;
          const badgeH = 20;
          const badgeX = n.screenX - badgeW / 2;
          const badgeY = n.screenY - radius - 24;

          // Glassmorphic capsule background
          ctx.fillStyle = "rgba(10, 15, 29, 0.88)";
          ctx.strokeStyle = n.color;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 10);
          ctx.fill();
          ctx.stroke();

          // Small glowing color dot inside badge
          ctx.fillStyle = n.color;
          ctx.beginPath();
          ctx.arc(badgeX + 10, badgeY + badgeH / 2, 3, 0, Math.PI * 2);
          ctx.fill();

          // Anchor title text
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "left";
          ctx.fillText(n.title, badgeX + 17, badgeY + 14);
        } else if (n.isSelected || n.isHovered || showAllLabels) {
          // Individual node tooltip label (only shown on hover or when showAllLabels is toggled)
          ctx.font = `${n.isSelected || n.isHovered ? "600 11px" : "500 9.5px"} Inter, system-ui, sans-serif`;
          ctx.fillStyle = n.isSelected || n.isHovered ? "#ffffff" : "rgba(203, 213, 225, 0.75)";
          ctx.textAlign = "center";
          ctx.shadowColor = "#000000";
          ctx.shadowBlur = 4;
          ctx.fillText(n.title, n.screenX, n.screenY - radius - 7);
          ctx.shadowBlur = 0;
        }

        ctx.restore();
      });

      ctx.restore();

      animationId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [domain, visibleNodes, selectedNode, hoveredNode, isCinemaMode, isPlayingDemo, showAllLabels, project3D]);

  // Mouse Interaction handlers for 3D Orbit & Pan
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = true;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    hasMovedSignificantlyRef.current = false;
    inertiaRef.current = { vx: 0, vy: 0 };
    if (isPlayingDemo) setIsPlayingDemo(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (isDraggingRef.current) {
      const dx = e.clientX - lastMousePosRef.current.x;
      const dy = e.clientY - lastMousePosRef.current.y;

      if (Math.hypot(e.clientX - dragStartPosRef.current.x, e.clientY - dragStartPosRef.current.y) > 4) {
        hasMovedSignificantlyRef.current = true;
      }

      // If middle button (button 1) or right button (button 2) or Shift key held: PAN
      if (e.buttons === 4 || e.buttons === 2 || e.shiftKey) {
        panOffsetRef.current.x += dx;
        panOffsetRef.current.y += dy;
      } else {
        // Orbit Rotation
        rotationRef.current.rotY += dx * 0.0055;
        rotationRef.current.rotX = Math.max(-1.45, Math.min(1.45, rotationRef.current.rotX + dy * 0.0055));
        inertiaRef.current = { vx: dx * 0.004, vy: dy * 0.004 };
      }

      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    } else {
      // Hover Detection
      const hit = findNodeAtPosition(mouseX, mouseY, 18);
      setHoveredNode(hit);
      if (hit) {
        setHoverPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      } else {
        setHoverPos(null);
      }
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  // Zoom with Mouse Wheel
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.08 : 0.92;
    zoomRef.current = Math.max(0.35, Math.min(4.0, zoomRef.current * factor));
  };

  // Touch Support (Pinch zoom & Drag orbit)
  const touchStartRef = useRef<{ x: number; y: number; dist?: number }>({ x: 0, y: 0 });

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      isDraggingRef.current = true;
      hasMovedSignificantlyRef.current = false;
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY };
      lastMousePosRef.current = { x: t.clientX, y: t.clientY };
      if (isPlayingDemo) setIsPlayingDemo(false);
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchStartRef.current.dist = dist;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1 && isDraggingRef.current) {
      const t = e.touches[0];
      const dx = t.clientX - lastMousePosRef.current.x;
      const dy = t.clientY - lastMousePosRef.current.y;
      if (Math.hypot(t.clientX - touchStartRef.current.x, t.clientY - touchStartRef.current.y) > 5) {
        hasMovedSignificantlyRef.current = true;
      }
      rotationRef.current.rotY += dx * 0.006;
      rotationRef.current.rotX = Math.max(-1.45, Math.min(1.45, rotationRef.current.rotX + dy * 0.006));
      lastMousePosRef.current = { x: t.clientX, y: t.clientY };
    } else if (e.touches.length === 2 && touchStartRef.current.dist) {
      const newDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const factor = newDist / touchStartRef.current.dist;
      zoomRef.current = Math.max(0.35, Math.min(4.0, zoomRef.current * (1 + (factor - 1) * 0.6)));
      touchStartRef.current.dist = newDist;
    }
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
    touchStartRef.current.dist = undefined;
  };

  const closeNodePanel = () => {
    userClosedPanelRef.current = true;
    setSelectedNode(null);
    setIsEditingContent(false);
  };

  // Click on Canvas to pick node (only if not dragged); empty space closes panel
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (hasMovedSignificantlyRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const hit = findNodeAtPosition(mouseX, mouseY, 28);
    if (hit) {
      userClosedPanelRef.current = false;
      setSelectedNode(hit);
      setEditedMarkdown(hit.content);
    } else {
      closeNodePanel();
    }
  };

  const handleSaveMarkdown = () => {
    if (!selectedNode) return;
    selectedNode.content = editedMarkdown;
    setIsEditingContent(false);
    toast.success(`✓ Archivo "${selectedNode.filename}" actualizado en el Segundo Cerebro.`);
  };

  return (
    <div ref={containerRef} className="relative w-full h-full min-h-0 bg-[#050811] text-white overflow-hidden select-none font-sans">
      
      {/* 1. TOP HEADER BAR */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-b from-[#050811] via-[#050811]/90 to-transparent">
        
        {/* Left: Brand Identity & Dual Brain Switcher */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full animate-pulse shrink-0 ${domain === "aura" ? "bg-purple-400 shadow-[0_0_10px_#a855f7]" : "bg-cyan-400 shadow-[0_0_10px_#00e5ff]"}`} />
            <h1 className="text-xs sm:text-base font-bold tracking-tight text-white truncate">
              {domain === "aura" ? "Aura Brain" : "Prometheus"}
            </h1>
          </div>

          {/* Quick Dual Switcher Pill */}
          <div className="flex items-center bg-[#0d1322] p-0.5 sm:p-1 rounded-full border border-slate-800 text-[10px] sm:text-xs shrink-0">
            <button
              onClick={() => setDomain("aura")}
              className={`px-2 sm:px-3 py-1 rounded-full font-bold transition-all ${domain === "aura" ? "bg-purple-500 text-white shadow-md shadow-purple-500/20" : "text-slate-400 hover:text-white"}`}
            >
              <span className="sm:hidden">Vida</span>
              <span className="hidden sm:inline">✨ Vida</span>
            </button>
            <button
              onClick={() => setDomain("prometheus")}
              className={`px-2 sm:px-3 py-1 rounded-full font-bold transition-all ${domain === "prometheus" ? "bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20" : "text-slate-400 hover:text-white"}`}
            >
              <span className="sm:hidden">Taller</span>
              <span className="hidden sm:inline">🛠️ Taller</span>
            </button>
          </div>
        </div>

        {/* Center: Search Bar with [/] shortcut */}
        <div className="relative w-96 max-w-full hidden md:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes, people, videos, skills..."
            className="w-full bg-[#0d1322]/80 border border-slate-800/80 rounded-full pl-9 pr-8 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-purple-500/60 transition-all shadow-inner"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
            /
          </kbd>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowWhatsInHere(!showWhatsInHere)}
            className="h-7 sm:h-8 text-xs font-semibold border-slate-800 bg-[#0d1322]/80 text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-lg gap-1 sm:gap-1.5 px-2 sm:px-3"
          >
            <HelpCircle className="w-3.5 h-3.5 text-purple-400" />
            <span className="hidden sm:inline">What's in here</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              rotationRef.current = { rotX: 0.22, rotY: 0.65 };
              zoomRef.current = 1.0;
              panOffsetRef.current = { x: 0, y: 0 };
              toast.info("Cámara restablecida al centro de la galaxia.");
            }}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-slate-400 hover:text-white rounded-lg"
            title="Recalibrar vista"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </header>

      {/* Top Right Notes Count Pill — desktop only */}
      <div className="absolute top-14 sm:top-16 right-3 sm:right-6 z-20 text-[10px] sm:text-[11px] text-slate-400 hidden sm:flex items-center gap-1.5 bg-[#0d1322]/60 backdrop-blur-md px-3 py-1 rounded-full border border-slate-800/60">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
        {visibleNodes.length} notes in view
      </div>

      {/* 2. LEFT HERO OVERLAY — desktop; mobile gets a quiet strip */}
      <div className="absolute top-12 left-3 right-3 z-20 pointer-events-none sm:hidden">
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="font-bold text-white tabular-nums">{visibleNodes.length}</span>
          <span>notas</span>
          <span className="text-slate-600">·</span>
          <span className="font-bold text-white tabular-nums">{currentSources.filter(s => s.active).length}</span>
          <span>fuentes</span>
          <span className="text-slate-600">·</span>
          <span className="font-bold text-white tabular-nums">{totalConnections.toLocaleString()}</span>
          <span>links</span>
        </div>
      </div>
      <div className="absolute top-20 left-6 z-20 pointer-events-none max-w-sm hidden sm:block">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
          <span className={`w-2 h-2 rounded-full ${domain === "aura" ? "bg-purple-400" : "bg-cyan-400"}`} />
          A LIVING KNOWLEDGE SYSTEM
        </div>
        <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.05]">
          Every idea.<br />
          <span className={domain === "aura" ? "text-purple-400" : "text-cyan-400"}>Connected.</span>
        </h2>
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          {domain === "aura"
            ? "Your personal life. Your daily habits. One harmonious operating system."
            : "Your business. Your experience. One expanding workshop universe."}
        </p>

        {/* Hero Stats */}
        <div className="flex items-center gap-6 mt-4 pointer-events-auto">
          <div>
            <div className="text-xl font-black text-white">{visibleNodes.length}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">NOTES</div>
          </div>
          <div>
            <div className="text-xl font-black text-white">{currentSources.filter(s => s.active).length}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">SOURCES</div>
          </div>
          <div>
            <div className="text-xl font-black text-white">{totalConnections.toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">CONNECTIONS</div>
          </div>
        </div>
      </div>

      {/* 3. LEFT BOTTOM "SOURCES" — desktop card; mobile uses sheet toggle below */}
      <div className="absolute bottom-6 left-6 z-20 w-64 bg-[#0d1322]/90 backdrop-blur-xl p-4 rounded-2xl border border-slate-800/80 shadow-2xl space-y-3 hidden sm:block">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
          <span>SOURCES</span>
          <span className="text-[9px] text-slate-500">Filtrar Galaxia</span>
        </div>

        {/* Source Categories List */}
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {currentSources.map((source) => (
            <button
              key={source.id}
              onClick={() => toggleSource(source.id)}
              className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs transition-all ${
                source.active
                  ? "hover:bg-slate-800/60 text-slate-200"
                  : "opacity-40 hover:opacity-70 text-slate-500 line-through"
              }`}
            >
              <div className="flex items-center gap-2 truncate">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: source.color }} />
                <span className="truncate text-left text-[11px] font-medium">{source.name}</span>
              </div>
              <span className="text-[10px] font-mono text-slate-400 shrink-0 ml-2">{source.count}</span>
            </button>
          ))}
        </div>

        {/* Checkboxes */}
        <div className="pt-2 border-t border-slate-800/80 space-y-2">
          <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer hover:text-white">
            <input
              type="checkbox"
              checked={onlyNeedsAttention}
              onChange={(e) => setOnlyNeedsAttention(e.target.checked)}
              className="rounded bg-slate-900 border-slate-700 text-purple-500 focus:ring-0"
            />
            Only what needs attention
          </label>
          <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer hover:text-white">
            <input
              type="checkbox"
              checked={showAllLabels}
              onChange={(e) => setShowAllLabels(e.target.checked)}
              className="rounded bg-slate-900 border-slate-700 text-purple-500 focus:ring-0"
            />
            Show all labels
          </label>
        </div>
      </div>

      {/* 4. MAIN FULLSCREEN 3D WEBGL/CANVAS VIEWPORT */}
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleCanvasClick}
        className={`w-full h-full block ${isDraggingRef.current ? "cursor-grabbing" : hoveredNode ? "cursor-pointer" : "cursor-grab"}`}
      />

      {/* Hover Floating Tooltip */}
      {hoveredNode && hoverPos && !isDraggingRef.current && (
        <div 
          className="absolute pointer-events-none z-25 bg-[#0d1322]/95 backdrop-blur-md border border-slate-700/80 px-3 py-1.5 rounded-lg shadow-xl text-xs text-white transform -translate-x-1/2 -translate-y-full mb-2"
          style={{ left: hoverPos.x, top: hoverPos.y }}
        >
          <div className="font-bold flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hoveredNode.color }} />
            <span>{hoveredNode.title}</span>
              {(hoveredNode.isLiveMemory || hoveredNode.isLiveSaved) && (
              <span className="text-[9px] bg-cyan-500/25 text-cyan-300 px-1.5 py-0.2 rounded border border-cyan-500/40 font-semibold">
                ✨ En Vivo
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-400">{hoveredNode.sourceName}</div>
        </div>
      )}

      {/* Mobile sources sheet */}
      {showSourcesMobile && (
        <div className="absolute inset-x-3 bottom-16 z-30 sm:hidden bg-[#0d1322]/95 backdrop-blur-xl p-3 rounded-2xl border border-slate-800/80 shadow-2xl space-y-2 max-h-[42%] overflow-y-auto">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
            <span>Fuentes</span>
            <button type="button" onClick={() => setShowSourcesMobile(false)} className="p-1 text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-1">
            {currentSources.map((source) => (
              <button
                key={source.id}
                type="button"
                onClick={() => toggleSource(source.id)}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs transition-all ${
                  source.active
                    ? "hover:bg-slate-800/60 text-slate-200"
                    : "opacity-40 text-slate-500 line-through"
                }`}
              >
                <div className="flex items-center gap-2 truncate">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: source.color }} />
                  <span className="truncate text-left text-[11px] font-medium">{source.name}</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 shrink-0 ml-2">{source.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 5. BOTTOM BAR CONTROLS */}
      <div className="absolute bottom-3 sm:bottom-6 inset-x-3 sm:inset-x-auto sm:right-6 z-20 flex items-center justify-between sm:justify-end gap-2">
        <button
          type="button"
          onClick={() => setShowSourcesMobile((v) => !v)}
          className="sm:hidden px-3 py-1.5 rounded-full text-[11px] font-bold bg-[#0d1322]/90 border border-slate-800/80 text-slate-200"
        >
          Fuentes
        </button>

        <span className="text-[11px] text-slate-500 hidden sm:inline">
          Drag to orbit · Scroll to explore · Shift+drag to pan
        </span>

        <div className="flex items-center gap-1.5 bg-[#0d1322]/90 backdrop-blur-xl p-1 sm:p-1.5 rounded-full border border-slate-800/80 shadow-2xl">
          <button
            onClick={() => {
              setIsPlayingDemo(!isPlayingDemo);
              if (!isPlayingDemo) setIsCinemaMode(false);
            }}
            className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold transition-all ${
              isPlayingDemo
                ? "bg-purple-500 text-white shadow-md shadow-purple-500/30"
                : "text-slate-300 hover:text-white hover:bg-slate-800/60"
            }`}
          >
            {isPlayingDemo ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-current" />}
            <span className="hidden min-[360px]:inline">Demo</span>
          </button>
          <button
            onClick={() => {
              setIsCinemaMode(!isCinemaMode);
              if (!isCinemaMode) setIsPlayingDemo(false);
            }}
            className={`px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold transition-all ${
              isCinemaMode
                ? "bg-slate-800 text-white border border-slate-700"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Cinema
          </button>
        </div>

        <div className="hidden sm:block text-[10px] font-mono tracking-widest text-slate-500 bg-slate-900/60 px-2.5 py-1 rounded-md border border-slate-800/60">
          {domain === "aura" ? "AURA BRAIN / 3D" : "PROMETHEUS BRAIN / 3D"}
        </div>
      </div>

      {/* 6. RIGHT HUD DRAWER: NODE INSPECTION & LIVE EDITING */}
      {selectedNode && (
        <div className="absolute top-12 sm:top-16 inset-x-3 sm:inset-x-auto sm:right-6 bottom-14 sm:bottom-16 z-40 w-auto sm:w-96 max-w-none sm:max-w-[calc(100vw-3rem)] bg-[#0d1322]/95 backdrop-blur-2xl border border-slate-800/90 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
          
          {/* Header */}
          <div className="p-4 border-b border-slate-800/80 flex items-start justify-between bg-slate-900/40">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: selectedNode.color }} />
                <Badge variant="outline" className="text-[10px] border-slate-700 bg-slate-800/60 text-slate-300">
                  {selectedNode.sourceName}
                </Badge>
                {(selectedNode.isLiveMemory || selectedNode.isLiveSaved) && (
                  <Badge className="text-[9px] bg-cyan-500/20 text-cyan-300 border-cyan-500/40 font-bold">
                    ✨ Recuerdo en Vivo (Base de Datos)
                  </Badge>
                )}
              </div>
              <h3 className="text-base font-bold text-white tracking-tight">{selectedNode.title}</h3>
              <p className="text-[11px] font-mono text-slate-400 truncate max-w-[280px]">{selectedNode.filename}</p>
              {selectedNode.created_at && (
                <p className="text-[10px] text-cyan-400/80 font-mono">
                  Registrado: {new Date(selectedNode.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeNodePanel();
              }}
              className="shrink-0 p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/60 transition-colors cursor-pointer"
              aria-label="Cerrar panel"
              title="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs leading-relaxed">
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">Descripción</span>
              <p className="text-slate-300 bg-slate-900/50 p-2.5 rounded-xl border border-slate-800/60">
                {selectedNode.description}
              </p>
            </div>

            {/* Markdown Content Viewer / Editor */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1.5">
                  <FileText className="w-3 h-3 text-purple-400" />
                  Contenido Markdown
                </span>
                <button
                  onClick={() => setIsEditingContent(!isEditingContent)}
                  className="text-[10px] text-purple-400 hover:underline font-semibold flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" />
                  {isEditingContent ? "Ver Previa" : "Editar Nota"}
                </button>
              </div>

              {isEditingContent ? (
                <div className="space-y-2">
                  <textarea
                    value={editedMarkdown}
                    onChange={(e) => setEditedMarkdown(e.target.value)}
                    rows={10}
                    className="w-full bg-[#050811] border border-purple-500/50 rounded-xl p-3 font-mono text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none leading-relaxed"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveMarkdown}
                    className="w-full bg-purple-500 hover:bg-purple-400 text-white font-bold text-xs h-8 rounded-xl shadow-lg shadow-purple-500/20 gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Guardar Cambios en Bóveda
                  </Button>
                </div>
              ) : (
                <div className="bg-[#050811] border border-slate-800/80 rounded-xl p-3 font-mono text-[11px] text-slate-300 whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed shadow-inner">
                  {selectedNode.content}
                </div>
              )}
            </div>

            {/* Sinapsis / Conexiones */}
            {selectedNode.connections && selectedNode.connections.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t border-slate-800/60">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">
                  Sinapsis Conectadas ({selectedNode.connections.length})
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedNode.connections.map((cId) => {
                    const target = allNodes.find((n) => n.id === cId);
                    return (
                      <button
                        key={cId}
                        onClick={() => {
                          if (target) {
                            userClosedPanelRef.current = false;
                            setSelectedNode(target);
                            setEditedMarkdown(target.content);
                          }
                        }}
                        className="px-2 py-1 rounded-md bg-slate-900 border border-slate-800 hover:border-purple-500/50 text-[10px] text-slate-300 hover:text-white transition-all flex items-center gap-1"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                        {target ? target.title : cId}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7. MODAL: "WHAT'S IN HERE" */}
      {showWhatsInHere && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-[#0d1322] border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                <h3 className="text-base font-bold text-white">Segundo Cerebro 3D (Arquitectura Nate Herk & Karpathy)</h3>
              </div>
              <button onClick={() => setShowWhatsInHere(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs text-slate-300 space-y-3 leading-relaxed">
              <p>
                Este universo tridimensional visualiza el conocimiento dinámico organizado bajo el principio de <strong>The Four Cs</strong> (Contexto, Conexiones, Capacidades y Cadencia) y <strong>LLM Wiki</strong>.
              </p>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800/80">
                  <div className="font-bold text-purple-400 mb-1">✨ Aura Brain</div>
                  <p className="text-[11px] text-slate-400">
                    Dedicado a bienestar personal, hábitos (agua, lectura, guitarra), fotos Nano Aventuras y productividad.
                  </p>
                </div>
                <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800/80">
                  <div className="font-bold text-cyan-400 mb-1">🛠️ Prometheus Brain</div>
                  <p className="text-[11px] text-slate-400">
                    Dedicado a hardware, CAD, Slicer, electrónica ESP32, física ASCE 7 y abastos en Puerto Rico.
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 italic">
                * Navegación: Arrastra el ratón para orbitar en 3D, usa la rueda para hacer zoom, mantén Shift para paneo y haz clic en cualquier nodo para inspeccionar y editar su Markdown en vivo.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                size="sm"
                onClick={() => setShowWhatsInHere(false)}
                className="bg-purple-500 hover:bg-purple-400 text-white font-bold text-xs"
              >
                Entendido
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SecondBrain3DStudio;
